export const PLAN_LOAD_TIMEOUT_MS = 6000

const trialDaysYearlyWith = parseInt(process.env.REACT_APP_PADDLE_TRIAL_DAYS_YEARLY_WITH_TRIAL || '7', 10)
const trialDaysYearlyWithout = parseInt(process.env.REACT_APP_PADDLE_TRIAL_DAYS_YEARLY_WITHOUT_TRIAL || '0', 10)
const trialDaysMonthly = parseInt(process.env.REACT_APP_PADDLE_TRIAL_DAYS_MONTHLY || '0', 10)

/**
 * Env display prices are US-style (`$11.99`), so a `[^0-9.]` strip + parseFloat
 * is safe here. The Paddle-driven path returns its own numeric `amount` from
 * `totals.total` (minor units) instead of parsing localized strings.
 */
function envDisplayToAmount(display: string): number {
  return parseFloat((display || '').replace(/[^0-9.]/g, '')) || 0
}

// Trial-aware fallback so the optimistic seed in `Metsikon` matches the plan ID
// that `SignInManager.getYearlyPlan()` would resolve for the same user (the
// yearly plan ID differs based on whether the user already had a free trial).
function buildFallbackPlanDetail(
  hadTrial: boolean,
  monthPriceDisplay: string,
  monthId: string | undefined,
  yearPriceDisplay: string,
  yearIdWithTrial: string | undefined,
  yearIdWithoutTrial: string | undefined
) {
  const yearlyTrialDays = hadTrial ? trialDaysYearlyWithout : trialDaysYearlyWith
  const monthDisplay = process.env.REACT_APP_PADDLE_DISPLAY_PRICE_MONTHLY || '$11.99'
  const yearDisplay = process.env.REACT_APP_PADDLE_DISPLAY_PRICE_YEARLY || '$49.99'
  return {
    month: {
      price: monthDisplay,
      priceAmount: envDisplayToAmount(monthDisplay),
      id: process.env.REACT_APP_PADDLE_PRICE_MONTHLY,
      canFreeTrial: trialDaysMonthly > 0,
      trialDays: trialDaysMonthly,
    },
    year: {
      price: yearDisplay,
      priceAmount: envDisplayToAmount(yearDisplay),
      id: hadTrial
        ? process.env.REACT_APP_PADDLE_PRICE_YEARLY_WITHOUT_TRIAL
        : process.env.REACT_APP_PADDLE_PRICE_YEARLY_WITH_TRIAL,
      canFreeTrial: yearlyTrialDays > 0,
      trialDays: yearlyTrialDays,
    },
  }
}

export function getFallbackPlanDetail(hadTrial: boolean) {
  return buildFallbackPlanDetail(
    hadTrial,
    process.env.REACT_APP_PADDLE_DISPLAY_PRICE_MONTHLY || '$11.99',
    process.env.REACT_APP_PADDLE_PRICE_MONTHLY,
    process.env.REACT_APP_PADDLE_DISPLAY_PRICE_YEARLY || '$49.99',
    process.env.REACT_APP_PADDLE_PRICE_YEARLY_WITH_TRIAL,
    process.env.REACT_APP_PADDLE_PRICE_YEARLY_WITHOUT_TRIAL
  )
}

export const FALLBACK_PLAN_DETAIL = getFallbackPlanDetail(false)

export function getFallbackSuperPlanDetail() {
  const monthDisplay = process.env.REACT_APP_PADDLE_DISPLAY_PRICE_SUPER_MONTHLY || '$21.99'
  const yearDisplay = process.env.REACT_APP_PADDLE_DISPLAY_PRICE_SUPER_YEARLY || '$191.99'
  return {
    month: {
      price: monthDisplay,
      priceAmount: envDisplayToAmount(monthDisplay),
      id: process.env.REACT_APP_PADDLE_PRICE_BAZAART_SUPER_MONTHLY,
      canFreeTrial: false,
      trialDays: 0,
    },
    year: {
      price: yearDisplay,
      priceAmount: envDisplayToAmount(yearDisplay),
      id: process.env.REACT_APP_PADDLE_PRICE_BAZAART_SUPER_YEARLY,
      canFreeTrial: false,
      trialDays: 0,
    },
  }
}

export const FALLBACK_SUPER_PLAN_DETAIL = getFallbackSuperPlanDetail()
/**
 * Formats a numeric amount as a localized currency string. Prefers the
 * customer's country (from Paddle PricePreview) over the browser locale so
 * derived values (e.g. yearly-from-monthly, per-month) format consistently
 * with Paddle's headline string. Falls back to a US-style `$X.YY` rendering
 * when no currency is known.
 */
export function formatPlanPrice(
  amount: number,
  currencyCode?: string,
  countryCode?: string,
): string {
  const usdFallback = `$${amount.toFixed(2)}`
  if (!currencyCode) {
    return usdFallback
  }
  // `und-FR` is a valid BCP-47 tag (undefined language, region FR) that yields
  // French-style number formatting in modern engines. The chained try/catches
  // guard older runtimes that reject `und-*` and invalid ISO 4217 codes that
  // make Intl.NumberFormat throw `RangeError`.
  const locale = countryCode ? `und-${countryCode}` : undefined
  try {
    return new Intl.NumberFormat(locale, { style: 'currency', currency: currencyCode }).format(amount)
  } catch {
    try {
      return new Intl.NumberFormat(undefined, { style: 'currency', currency: currencyCode }).format(amount)
    } catch {
      return usdFallback
    }
  }
}
